import * as React from 'react';
import { ArrowDownIcon, ArrowUpIcon, MoreHorizontalIcon, PencilIcon, PinIcon, PinOffIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  MAX_FAVOURITE_LABEL_LENGTH,
  MAX_NAVIGATION_FAVOURITES,
  type NavigationFavouritesState
} from '@/lib/navigation-favourites';
import { cn } from '@/lib/utils';

/** Shared by every menu row so desktop and mobile panels stay in sync with one store. */
export const NavigationFavouritesContext = React.createContext<NavigationFavouritesState | null>(null);

const trailingButton = 'absolute top-1/2 right-1 size-6 -translate-y-1/2 rounded-sm';

/**
 * Pin toggle shown on every pinnable menu row. An outlined, tilted pin means
 * "can be pinned"; a filled upright pin means it is already a favourite.
 */
export function PinToggle({ path, title }: { path: string; title: string }) {
  const favourites = React.useContext(NavigationFavouritesContext);
  if (!favourites?.available) return null;
  const pinned = favourites.isPinned(path);
  const full = !pinned && favourites.isFull;
  const label = pinned
    ? `Unpin ${title} from favourites`
    : full
      ? `Favourites are full (${MAX_NAVIGATION_FAVOURITES}). Unpin one to pin ${title}`
      : `Pin ${title} to favourites`;

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={label}
      aria-pressed={pinned}
      title={label}
      disabled={full}
      onClick={() => (pinned ? favourites.unpin(path) : favourites.pin({ path, label: title }))}
      className={cn(
        trailingButton,
        pinned
          ? 'text-foreground'
          : 'text-muted-foreground/40 group-hover/nav:text-muted-foreground hover:text-foreground focus-visible:text-foreground'
      )}
    >
      <PinIcon aria-hidden className={cn('size-3.5', pinned ? 'fill-current' : 'rotate-45')} />
    </Button>
  );
}

/** Rename, reorder or remove one favourite from the Favourites section. */
export function FavouriteActions({
  path,
  label,
  previousPath,
  nextPath
}: {
  path: string;
  label: string;
  previousPath?: string;
  nextPath?: string;
}) {
  const favourites = React.useContext(NavigationFavouritesContext);
  const [renaming, setRenaming] = React.useState(false);
  if (!favourites?.available) return null;

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`Manage favourite ${label}`}
            title={`Manage favourite ${label}`}
            className={cn(trailingButton, 'text-muted-foreground hover:text-foreground')}
          >
            <MoreHorizontalIcon aria-hidden className="size-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="right" align="start">
          <DropdownMenuItem onSelect={() => setRenaming(true)}>
            <PencilIcon aria-hidden className="size-4" />
            Rename
          </DropdownMenuItem>
          <DropdownMenuItem disabled={!previousPath} onSelect={() => previousPath && favourites.swap(path, previousPath)}>
            <ArrowUpIcon aria-hidden className="size-4" />
            Move up
          </DropdownMenuItem>
          <DropdownMenuItem disabled={!nextPath} onSelect={() => nextPath && favourites.swap(path, nextPath)}>
            <ArrowDownIcon aria-hidden className="size-4" />
            Move down
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => favourites.unpin(path)}>
            <PinOffIcon aria-hidden className="size-4" />
            Remove from favourites
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {renaming && (
        <RenameFavouriteDialog
          label={label}
          onCancel={() => setRenaming(false)}
          onSave={(next) => {
            favourites.rename(path, next);
            setRenaming(false);
          }}
        />
      )}
    </>
  );
}

function RenameFavouriteDialog({
  label,
  onCancel,
  onSave
}: {
  label: string;
  onCancel: () => void;
  onSave: (label: string) => void;
}) {
  const [value, setValue] = React.useState(label);
  const inputId = React.useId();
  const errorId = React.useId();
  const trimmed = value.trim();
  const error = trimmed ? null : 'Enter a name for this favourite.';

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onCancel(); }}>
      <DialogContent className="sm:max-w-sm">
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!error) onSave(trimmed);
          }}
        >
          <DialogHeader>
            <DialogTitle>Rename favourite</DialogTitle>
            <DialogDescription>Choose the name shown in your Favourites menu. The screen itself is unchanged.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label htmlFor={inputId}>Name</Label>
            <Input
              id={inputId}
              value={value}
              maxLength={MAX_FAVOURITE_LABEL_LENGTH}
              autoFocus
              aria-invalid={Boolean(error)}
              aria-describedby={error ? errorId : undefined}
              onChange={(event) => setValue(event.target.value)}
            />
            {error && <p id={errorId} className="text-destructive text-sm">{error}</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
            <Button type="submit" disabled={Boolean(error)}>Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
